import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography, Card, Chip, CircularProgress, Link } from '@mui/material';

// Returns null on broken image — no empty placeholder rings
function TeamLogo({ src, size = 20 }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <Box component="img" src={src} alt=""
      onError={() => setFailed(true)}
      sx={{ width: size, height: size, borderRadius: '3px', objectFit: 'contain', flexShrink: 0 }}
    />
  );
}

const CATEGORY_COLORS = {
  'ANALYTICS':    '#2ea84b',
  'FILM ROOM':    '#3b82f6',
  'TACTICAL':     '#8b5cf6',
  'FREE AGENCY':  '#f5a623',
  'DRAFT':        '#ef4444',
  'DRAFT PREP':   '#ef4444',
  'GRADES':       '#f5a623',
  'INJURY':       '#f97316',
  'TRANSACTION':  '#64748b',
  'PREVIEW':      '#0ea5e9',
  'RECAP':        '#10b981',
  'RANKINGS':     '#a855f7',
};

function getCatColor(cat) {
  const up = (cat || '').toUpperCase();
  for (const [k, v] of Object.entries(CATEGORY_COLORS)) {
    if (up.includes(k)) return v;
  }
  return '#9eb8d0';
}

// Extract NFL team abbreviation from ESPN team URL
function nflLogoFromCategory(cat) {
  const href = cat?.team?.links?.web?.teams?.href || '';
  const m = href.match(/\/name\/([^/]+)\//);
  if (m) return `https://a.espncdn.com/i/teamlogos/nfl/500/${m[1]}.png`;
  // fallback: use team abbreviation if available
  const abbr = cat?.team?.abbreviation;
  if (abbr) return `https://a.espncdn.com/i/teamlogos/nfl/500/${abbr.toLowerCase()}.png`;
  return null;
}

// Extract college team logo from teamId
function collegeLogoFromCategory(cat) {
  if (cat?.teamId) return `https://a.espncdn.com/i/teamlogos/ncaa/500/${cat.teamId}.png`;
  return null;
}

function parseArticles(articles, sport) {
  return articles.map(a => {
    const teamCats = (a.categories || []).filter(c => c.type === 'team');
    const topicCat = (a.categories || []).find(c => c.type === 'topic');
    const category = topicCat?.description?.toUpperCase() ||
      (a.type || 'NEWS').toUpperCase().replace('STORY', 'NEWS');

    // Deduplicate — each team logo appears at most once per article
    const seen = new Set();
    const teams = teamCats.map(c => ({
      name: c.description,
      logo: sport === 'nfl' ? nflLogoFromCategory(c) : collegeLogoFromCategory(c),
    })).filter(t => {
      if (!t.logo || seen.has(t.logo)) return false;
      seen.add(t.logo);
      return true;
    });

    return {
      headline: a.headline || a.title || '',
      description: a.description || null,
      category,
      teams,
      image: a.images?.[0]?.url || null,
      link: a.links?.web?.href || '#',
      published: a.published
        ? new Date(a.published).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : null,
    };
  });
}

// Full-width featured card (first article) — image at top, big headline
function FeaturedNewsCard({ article }) {
  const catColor = getCatColor(article.category);
  return (
    <Card component={Link} href={article.link} target="_blank" rel="noopener noreferrer"
      underline="none"
      sx={{
        display: 'flex', flexDirection: 'column',
        background: '#131318',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 2, cursor: 'pointer', textDecoration: 'none',
        transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
        '&:hover': {
          borderColor: catColor + '88',
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 32px rgba(0,0,0,0.4)`,
        },
      }}>
      {article.image && (
        <Box component="img" src={article.image} alt=""
          sx={{ width: '100%', height: { xs: 180, sm: 220, md: 260 }, objectFit: 'cover', borderRadius: '8px 8px 0 0', opacity: 0.88 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}
      <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={article.category} size="small" sx={{
            height: 17, fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.04em',
            bgcolor: catColor + '1a', color: catColor, border: `1px solid ${catColor}44`,
          }} />
          {article.published && (
            <Typography sx={{ fontSize: '0.62rem', color: '#3a5070' }}>{article.published}</Typography>
          )}
        </Box>
        <Typography sx={{
          fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#e8f0f8', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {article.headline}
        </Typography>
        {article.description && (
          <Typography sx={{
            fontSize: '0.82rem', color: '#a0b0be', lineHeight: 1.65,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {article.description}
          </Typography>
        )}
        {article.teams.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pt: 0.5, flexWrap: 'wrap' }}>
            {article.teams.slice(0, 4).map((t, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <TeamLogo src={t.logo} size={18} />
                {article.teams.length <= 2 && t.name && (
                  <Typography sx={{ fontSize: '0.62rem', color: '#4a6080' }}>{t.name.split(' ').pop()}</Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}

// Full-width horizontal row card for all other articles
function NewsRowCard({ article }) {
  const catColor = getCatColor(article.category);
  return (
    <Card component={Link} href={article.link} target="_blank" rel="noopener noreferrer"
      underline="none"
      sx={{
        display: 'flex', flexDirection: 'row', alignItems: 'stretch',
        background: '#111116',
        border: '1px solid rgba(255,255,255,0.11)',
        borderRadius: 2, cursor: 'pointer', textDecoration: 'none', overflow: 'hidden',
        transition: 'border-color 0.15s, transform 0.12s',
        '&:hover': { borderColor: catColor + '66', transform: 'translateX(2px)', background: '#161619' },
      }}>
      {/* Left: thumbnail */}
      {article.image ? (
        <Box component="img" src={article.image} alt=""
          sx={{ width: { xs: 80, sm: 110 }, flexShrink: 0, objectFit: 'cover', opacity: 0.82 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        /* No image — colored left accent bar */
        <Box sx={{ width: 4, flexShrink: 0, bgcolor: catColor + '88' }} />
      )}

      {/* Right: text */}
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1.5, sm: 2 }, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={article.category} size="small" sx={{
            height: 16, fontSize: '0.56rem', fontWeight: 800, letterSpacing: '0.04em',
            bgcolor: catColor + '1a', color: catColor, border: `1px solid ${catColor}33`,
          }} />
          {article.published && (
            <Typography sx={{ fontSize: '0.59rem', color: '#3a5070' }}>{article.published}</Typography>
          )}
          {article.teams.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              {article.teams.slice(0, 3).map((t, i) => <TeamLogo key={i} src={t.logo} size={16} />)}
            </Box>
          )}
        </Box>
        <Typography sx={{
          fontWeight: 600, fontSize: { xs: '0.84rem', sm: '0.9rem' }, color: '#d0dce8', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          letterSpacing: '-0.01em',
        }}>
          {article.headline}
        </Typography>
        {article.description && (
          <Typography sx={{
            fontSize: '0.75rem', color: '#8e9eae', lineHeight: 1.55,
            display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {article.description}
          </Typography>
        )}
      </Box>
    </Card>
  );
}

let _nflNewsCache = null;
let _cfbNewsCache = null;
const NFL_NEWS_URL = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl/news?limit=21';
const NEWS_POLL_MS = 5 * 60 * 1000; // 5 minutes

export function NFLNewsGrid() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [live, setLive]         = useState(false);

  const fetchNews = (isInitial = false) => {
    axios.get(NFL_NEWS_URL, { timeout: 8000 })
      .then(r => {
        const parsed = parseArticles(r.data?.articles || [], 'nfl');
        _nflNewsCache = parsed;
        // Only update state if something changed (avoids unnecessary re-renders)
        setArticles(prev => {
          const prevIds = prev.map(a => a.id || a.headline).join(',');
          const nextIds = parsed.map(a => a.id || a.headline).join(',');
          return prevIds === nextIds ? prev : parsed;
        });
        setLive(true);
      })
      .catch(() => { if (isInitial) setLive(false); })
      .finally(() => { if (isInitial) setLoading(false); });
  };

  useEffect(() => {
    if (_nflNewsCache) { setArticles(_nflNewsCache); setLive(true); setLoading(false); }
    else fetchNews(true);
    const interval = setInterval(() => fetchNews(false), NEWS_POLL_MS);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress color="primary" /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#9eb8d0', letterSpacing: '0.06em' }}>
          📰 NFL NEWS
        </Typography>
        <Chip label={live ? 'Live · ESPN' : 'Preview'} size="small"
          sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700,
            bgcolor: live ? 'rgba(46,168,75,0.15)' : '#1a2e44',
            color: live ? '#2ea84b' : '#6a8aa8',
            border: live ? '1px solid #2ea84b44' : 'none' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {articles.map((a, i) =>
          i === 0
            ? <FeaturedNewsCard key={i} article={a} />
            : <NewsRowCard key={i} article={a} />
        )}
      </Box>
    </Box>
  );
}

export function CollegeNewsGrid() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [live, setLive]         = useState(false);

  useEffect(() => {
    if (_cfbNewsCache) { setArticles(_cfbNewsCache); setLive(true); setLoading(false); return; }
    axios.get('https://site.api.espn.com/apis/site/v2/sports/football/college-football/news?limit=21', { timeout: 8000 })
      .then(r => {
        const parsed = parseArticles(r.data?.articles || [], 'college');
        _cfbNewsCache = parsed;
        setArticles(parsed);
        setLive(true);
      })
      .catch(() => setLive(false))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress color="primary" /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#9eb8d0', letterSpacing: '0.06em' }}>
          📰 COLLEGE FOOTBALL NEWS
        </Typography>
        <Chip label={live ? 'Live · ESPN' : 'Preview'} size="small"
          sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700,
            bgcolor: live ? 'rgba(46,168,75,0.15)' : '#1a2e44',
            color: live ? '#2ea84b' : '#6a8aa8',
            border: live ? '1px solid #2ea84b44' : 'none' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {articles.map((a, i) =>
          i === 0
            ? <FeaturedNewsCard key={i} article={a} />
            : <NewsRowCard key={i} article={a} />
        )}
      </Box>
    </Box>
  );
}
